import { useEffect, useState } from 'react'

import { usePromotion } from '@mc/promo-utils'
import {
  trackCollapsedPaintedDoorViewed,
  trackI18nPricingViewed,
  trackI18nPricingV2Viewed,
} from '@mc/checkout'
import { Trans, useTranslation } from '@mc/i18n'
import { useStaticData } from '@mc/static-data-react'
import { useIsVisible } from '@mc/visibility'

import { StaticData } from 'types/libs/staticData'
import { RevampedPricingCopyExp } from 'experiments/pen-4819_v1_revamped_pricing_copy'

export type PricingTextProps = {
  translationKey?:
    | 'long'
    | 'short'
    | 'shortPriceBold'
    | 'shortPriceStyled'
    | 'longWithSeries'
    | 'longJustClasses'
    // pen-4734_v1 experiment
    | 'pen-4734_v1-nonPromoStickyFooterRedesign'
    | 'pen-4734_v1-nonPromoStickyFooterRedesign-mobile'
  children?: React.ReactNode
  className?: string
  useRevampedPricingCopy?: boolean
}

const config = {
  threshold: 0.7,
  duration: { visible: 500 },
}

export const PricingText = ({
  translationKey = 'long',
  children,
  className,
  useRevampedPricingCopy = false,
}: PricingTextProps) => {
  const { t } = useTranslation('@mc/next')
  const { annualPass, guestPass } = useStaticData<StaticData>()
  const { isPromotionOn } = usePromotion()

  const { ref } = useTrackingEntryPaintedDoorViewed()

  if (!annualPass) {
    return null
  }

  if (guestPass?.advocate) {
    return <span>{t('guestPass.pricing')}</span>
  }

  const defaultPricingText = () => (
    <Trans
      i18nKey={`common.pricing.${translationKey}`}
      values={{ price: annualPass?.monetizedYearPricePerMonth }}
      ns='@mc/next'
    >
      {children}
    </Trans>
  )

  let content: React.ReactNode

  if (isPromotionOn) {
    content = (
      <Trans
        i18nKey={`common.pricing.discount.${translationKey}`}
        values={{
          strikeThroughPrice: annualPass.monetizedYearPricePerMonth,
          price: annualPass.monetizedYearPricePerMonthDiscounted,
        }}
        ns='@mc/next'
      >
        <span className='mc-opacity--hinted mc-text--bold' />
        <span className='mc-opacity--hinted mc-text--strike' />
        <span className='mc-text--bold' />
      </Trans>
    )
  } else if (useRevampedPricingCopy) {
    content = (
      <RevampedPricingCopyExp
        control={defaultPricingText}
        monetizedYearPricePerMonth={annualPass.monetizedYearPricePerMonth}
      />
    )
  } else {
    content = defaultPricingText()
  }

  return (
    <span className={className} ref={ref}>
      {content}
    </span>
  )
}

const useTrackingEntryPaintedDoorViewed = () => {
  // prcng-107_v1 experiment
  // using the TrackingContainer is not possible here since this component
  // is being used inside <p> tags what triggers a react-hydration-error.
  // Workaround: replicate TrackingContainer behavior
  const { isVisible, ref } = useIsVisible<HTMLSpanElement>(config)
  const [isFirstEventFired, setIsFirstEventFired] = useState(false)
  useEffect(() => {
    if (isVisible && !isFirstEventFired) {
      setIsFirstEventFired(true)
      trackCollapsedPaintedDoorViewed()
      trackI18nPricingViewed()
      trackI18nPricingV2Viewed()
    }
  }, [isVisible, isFirstEventFired])

  return { ref }
}
